import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { modalOverlayProps } from '../utils/modalOverlayProps';
import './Users.css';

// Fonction pour obtenir l'URL de l'API dynamiquement
const getApiUrlDynamic = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '/api';
  }
  return 'http://localhost:8000/api';
};

const Users = () => {
  const { token, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchMailboxes();
  }, []);

  const fetchMailboxes = async () => {
    try {
      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/mailboxes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setMailboxes(data.mailboxes || []);
    } catch {}
  };

  const getMailboxForUser = (userId) => mailboxes.find(m => String(m.user_id) === String(userId)) || null;

  const togglePassword = (userId) => setVisiblePasswords(prev => ({ ...prev, [userId]: !prev[userId] }));

  const copyToClipboard = (text) => navigator.clipboard.writeText(text).catch(() => {});

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.status === 401) {
        setError(data.message || 'Token invalide ou expiré');
        await logout();
        return;
      }

      if (response.ok && data.success) {
        setUsers(data.users || []);
      } else {
        setError(data.message || 'Erreur lors du chargement des utilisateurs');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des utilisateurs:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getRoleBadgeClass = (roles) => {
    if (!roles || !Array.isArray(roles)) return 'role-badge';
    if (roles.includes('ROLE_ADMIN')) return 'role-badge role-admin';
    return 'role-badge role-user';
  };

  const getRoleLabel = (roles) => {
    if (!roles || !Array.isArray(roles)) return 'Utilisateur';
    if (roles.includes('ROLE_ADMIN')) return 'Administrateur';
    return 'Utilisateur';
  };

  const handleCreate = async (userData) => {
    try {
      setLoading(true);
      setError('');

      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowCreateModal(false);
        await fetchUsers();
        await fetchMailboxes();
      } else {
        setError(data.message || 'Erreur lors de la création de l\'utilisateur');
      }
    } catch (err) {
      console.error('Erreur lors de la création:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleUpdate = async (userData) => {
    try {
      setLoading(true);
      setError('');

      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowEditModal(false);
        setEditingUser(null);
        await fetchUsers();
      } else {
        setError(data.message || 'Erreur lors de la mise à jour de l\'utilisateur');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      setDeletingUserId(userId);
      setError('');

      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchUsers();
      } else {
        setError(data.message || 'Erreur lors de la suppression de l\'utilisateur');
      }
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setDeletingUserId(null);
    }
  };

  return (
    <div className="users-page">
      <div className="users-header">
        <div>
          <h1 className="users-title">👥 Gestion des utilisateurs</h1>
          <p className="users-subtitle">Liste de tous les utilisateurs du système</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            ➕ Créer un utilisateur
          </button>
          <button className="btn-secondary" onClick={fetchUsers} disabled={loading}>
            {loading ? '⏳ Chargement...' : '🔄 Actualiser'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des utilisateurs...</p>
        </div>
      ) : (
        <div className="users-content">
          <div className="users-stats">
            <div className="stat-card">
              <div className="stat-value">{users.length}</div>
              <div className="stat-label">Utilisateurs total</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {users.filter(u => u.roles?.includes('ROLE_ADMIN')).length}
              </div>
              <div className="stat-label">Administrateurs</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {users.filter(u => !u.roles?.includes('ROLE_ADMIN')).length}
              </div>
              <div className="stat-label">Utilisateurs</div>
            </div>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom d'utilisateur</th>
                  <th>Email</th>
                  <th>Boîte mail</th>
                  <th>Rôle</th>
                  <th>Date de création</th>
                  <th>Dernière connexion</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-state">
                      Aucun utilisateur trouvé
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        <div className="user-cell">
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.username} className="user-avatar-img" />
                          ) : (
                            <span className="user-avatar">👤</span>
                          )}
                          <span className="user-name">{user.username || 'Non défini'}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        {(() => {
                          const mb = getMailboxForUser(user.id);
                          if (!mb) return <span style={{ color: '#a0aec0', fontSize: '0.82rem' }}>—</span>;
                          return (
                            <div style={{ fontSize: '0.82rem' }}>
                              <div style={{ fontFamily: 'monospace', color: '#2d3748', marginBottom: '2px' }}>
                                {mb.email}
                                <button onClick={() => copyToClipboard(mb.email)} title="Copier" style={{ marginLeft: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#a0aec0', fontSize: '0.75rem' }}>📋</button>
                              </div>
                              <div style={{ color: '#718096', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                {visiblePasswords[user.id] ? mb.password_clear : '••••••••'}
                                <button onClick={() => togglePassword(user.id)} title="Afficher" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a0aec0', fontSize: '0.75rem' }}>
                                  {visiblePasswords[user.id] ? '🙈' : '👁'}
                                </button>
                                <button onClick={() => copyToClipboard(mb.password_clear)} title="Copier MDP" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a0aec0', fontSize: '0.75rem' }}>📋</button>
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        <span className={getRoleBadgeClass(user.roles)}>
                          {getRoleLabel(user.roles)}
                        </span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>{formatDate(user.last_login_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon btn-edit" 
                            onClick={() => handleEdit(user)}
                            title="Modifier"
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn-icon btn-delete" 
                            onClick={() => handleDelete(user.id)}
                            title="Supprimer"
                            disabled={deletingUserId === user.id}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de création */}
      {showCreateModal && (
        <UserModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
          title="Créer un utilisateur"
        />
      )}

      {/* Modal d'édition */}
      {showEditModal && editingUser && (
        <UserModal
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          onSave={handleUpdate}
          title="Modifier l'utilisateur"
          user={editingUser}
        />
      )}
    </div>
  );
};

// Composant Modal pour créer/modifier un utilisateur
const UserModal = ({ onClose, onSave, title, user = null }) => {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    username: user?.username || '',
    password: '',
    avatar: user?.avatar || '',
    roles: user?.roles || ['ROLE_USER']
  });
  const [formError, setFormError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.email || !formData.username) {
      setFormError('Email et nom d\'utilisateur sont requis');
      return;
    }

    if (!user && !formData.password) {
      setFormError('Le mot de passe est requis pour créer un utilisateur');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setFormError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    const userData = {
      email: formData.email,
      username: formData.username,
      roles: formData.roles,
      ...(formData.password && { password: formData.password }),
      avatar: formData.avatar || null
    };

    onSave(userData);
  };

  const toggleRole = (role) => {
    setFormData(prev => {
      const roles = prev.roles || [];
      if (roles.includes(role)) {
        // Ne pas permettre de retirer ROLE_USER
        if (role === 'ROLE_USER') return prev;
        return { ...prev, roles: roles.filter(r => r !== role) };
      } else {
        return { ...prev, roles: [...roles, role] };
      }
    });
  };

  return (
    <div className="modal-overlay" {...modalOverlayProps(onClose)}>
      <div className="modal-content" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {formError && <div className="form-error">{formError}</div>}

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={!!user}
            />
          </div>

          <div className="form-group">
            <label htmlFor="username">Nom d'utilisateur *</label>
            <input
              type="text"
              id="username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="avatar">Avatar (URL)</label>
            <input
              type="url"
              id="avatar"
              value={formData.avatar}
              onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
              placeholder="https://example.com/avatar.jpg"
            />
            {formData.avatar && (
              <div style={{ marginTop: '0.5rem' }}>
                <img 
                  src={formData.avatar} 
                  alt="Avatar preview" 
                  style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '50%', border: '2px solid #e2e8f0' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Mot de passe {!user && '*'}
              {user && <span className="form-hint">(laisser vide pour ne pas modifier)</span>}
            </label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!user}
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label>Rôles *</label>
            <div className="roles-checkboxes">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.roles.includes('ROLE_USER')}
                  onChange={() => toggleRole('ROLE_USER')}
                  disabled
                />
                <span>Utilisateur (obligatoire)</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.roles.includes('ROLE_ADMIN')}
                  onChange={() => toggleRole('ROLE_ADMIN')}
                />
                <span>Administrateur</span>
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              {user ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Users;

